import React, { useEffect, useRef } from "react";
import { View, Animated } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import StyledText from "./ui/styled-text";

export type ToastType = "success" | "error" | "info";

interface ToastNotificationProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onHide: () => void;
}

export function ToastNotification({
  message,
  type = "info",
  duration = 3000,
  onHide,
}: ToastNotificationProps) {
  const insets = useSafeAreaInsets();
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const showAnimation = Animated.timing(opacity, {
      toValue: 1,
      duration: 300,
      useNativeDriver: false,
    });

    const hideAnimation = Animated.timing(opacity, {
      toValue: 0,
      duration: 300,
      useNativeDriver: false,
    });

    const sequence = Animated.sequence([
      showAnimation,
      Animated.delay(duration - 600),
      hideAnimation,
    ]);

    sequence.start(({ finished }) => {
      if (finished) {
        onHide();
      }
    });

    return () => {
      sequence.stop();
    };
  }, [opacity, duration, onHide]);

  const getBackgroundColor = () => {
    switch (type) {
      case "success":
        return "bg-green-100 border-green-400";
      case "error":
        return "bg-red-100 border-red-400";
      default:
        return "bg-blue-100 border-blue-400";
    }
  };

  const getTextColor = () => {
    switch (type) {
      case "success":
        return "text-green-700";
      case "error":
        return "text-red-700";
      default:
        return "text-blue-700";
    }
  };

  return (
    <Animated.View
      style={{
        opacity,
        position: "absolute",
        bottom: insets.bottom + 16,
        left: 16,
        right: 16,
        zIndex: 100,
      }}
    >
      <View
        className={`p-4 border rounded-lg ${getBackgroundColor()} w-full max-w-xl mx-auto`}
      >
        <StyledText className={`text-center ${getTextColor()}`}>
          {message}
        </StyledText>
      </View>
    </Animated.View>
  );
}
